import './init-sentry';

import {
  autoPrefixTransformer,
  legacyLogicalPropertiesTransformer,
  StyleProvider,
} from '@ant-design/cssinjs';
import { ClassNames } from '@emotion/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';
import { isKyError } from 'ky';
import { uploadFile } from './features/file/api';
import { queryClient } from './query-client';
import { router } from './router';

export const App = () => {
  return (
    <StyleProvider
      hashPriority="high"
      transformers={[legacyLogicalPropertiesTransformer, autoPrefixTransformer]}
    >
      <QueryClientProvider client={queryClient}>
        <ClassNames>
          {({ css }) => (
            <ConfigProvider
              locale={zhCN}
              theme={{
                zeroRuntime: false,
                token: {
                  colorPrimary: '#171717',
                  colorTextBase: '#333333',
                  colorPrimaryBg: '#d7d7d7',
                  controlItemBgActiveHover: '#a0a0a0',
                },
                components: {
                  Button: {
                    colorPrimaryBgHover: '#adadad',
                  },
                  Grid: {
                    screenXXXL: 1920,
                    screenXXL: 1536,
                    screenXL: 1280,
                    screenLG: 1024,
                    screenMD: 768,
                    screenSM: 640,
                  },
                  Tooltip: {
                    colorBgSpotlight: '#ffffff',
                    colorTextLightSolid: '#171717',
                  },
                  Layout: {
                    headerBg: '#ffffff',
                    headerPadding: 16,
                  },
                },
              }}
              input={{
                allowClear: true,
              }}
              form={{
                classNames: {
                  root: css`
                    .ant-form-item-control {
                      flex: none !important;
                    }
                  `,
                },
              }}
              upload={{
                customRequest: async (options) => {
                  try {
                    const data = await uploadFile(options.file as File, {
                      onProgress: (event) => {
                        options.onProgress?.({
                          ...event,
                          percent: event.lengthComputable
                            ? Math.round((event.loaded / event.total) * 100)
                            : undefined,
                        });
                      },
                    });
                    options.onSuccess?.(data);
                  } catch (error) {
                    if (isKyError(error)) {
                      options.onError?.(error);
                    } else if (error instanceof Error) {
                      options.onError?.(error);
                    } else {
                      options.onError?.(new Error('发生未知错误，上传失败'));
                    }
                  }
                },
              }}
              select={{
                showSearch: true,
                allowClear: true,
              }}
            >
              <RouterProvider
                router={router}
                context={{ queryClient }}
              ></RouterProvider>
            </ConfigProvider>
          )}
        </ClassNames>
        <ReactQueryDevtools buttonPosition="bottom-left"></ReactQueryDevtools>
      </QueryClientProvider>
    </StyleProvider>
  );
};

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
